import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1; // Changed from 1.5 to 1 for consistency
const MOBILE_ZOOM = 2;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();

  /** @type {number} */ #scale = DEFAULT_ZOOM;
  /** @type {number} */ #initialDistance = 0;
  /** @type {number} */ #startScale = DEFAULT_ZOOM;
  /** @type {Point} */ #translate = { x: 0, y: 0 };
  /** @type {Point} */ #startPosition = { x: 0, y: 0 };
  /** @type {Point} */ #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */ #isDragging = false;
  /** @type {boolean} */ #isPinching = false; // Add pinch state
  /** @type {boolean} */ #initialized = false;
  /** @type {number | null} */ #animationFrame = null;

  #scrollParent = null;
  #wheelAccum = 0;
  #pollRafId = null;
  #pollingActive = false;
  #lastScrollPos = 0;
  #lastBoundingTop = null;
  #listenersAttached = false;
  #lastTapTime = 0;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;

    // Set initial CSS variables to ensure defaults
    this.style.setProperty('--drag-zoom-scale', '1');
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');

    this.#initResizeListener();
    this.#initEventListeners();

    // Only update transform after image loads to avoid premature calculations
    if (this.#image.complete) {
      this.#updateTransform();
    }
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;

    const { signal } = this.#controller;
    const options = { passive: false, signal };

    if (isMobileBreakpoint()) {
      this.addEventListener('touchstart', this.#handleTouchStart, options);
      this.addEventListener('touchmove', this.#handleTouchMove, options);
      this.addEventListener('touchend', this.#handleTouchEnd, options);
      // Add wheel support for dev tools pinch simulation on mobile
      this.addEventListener('wheel', this.#handleWheelZoom, options);
    } else {
      document.addEventListener('click', this.#delegatedClick, { capture: true, signal });
      // Add wheel support for desktop
      this.addEventListener('wheel', this.#handleWheelZoom, options);
    }

    // Always add image load listener
    this.#image?.addEventListener(
      'load',
      () => {
        this.#updateTransform();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#stopMonitoring();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized) {
      this.#initEventListeners();
    }
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #delegatedClick = (event) => {
    if (!this.contains(event.target)) return;
    this.#handleClickZoom(event);
  };

  #handleWheelZoom = (event) => {
    // Check if this is a pinch-to-zoom gesture (ctrlKey is set during trackpad pinch)
    if (event.ctrlKey || event.metaKey) {
      preventDefault(event);

      const rect = this.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      // Use mouse position if available, otherwise use center
      const zoomPoint = {
        x: event.offsetX || centerX,
        y: event.offsetY || centerY,
      };

      const scaleFactor = event.deltaY > 0 ? 0.9 : 1.1;
      const newScale = clamp(this.#scale * scaleFactor, MIN_ZOOM, MAX_ZOOM);

      if (newScale !== this.#scale) {
        // Calculate zoom around point
        const containerCenter = { x: centerX, y: centerY };
        const distanceFromCenter = {
          x: zoomPoint.x - containerCenter.x,
          y: zoomPoint.y - containerCenter.y,
        };

        const scaleDelta = newScale / this.#scale - 1.0;
        this.#translate.x -= (distanceFromCenter.x * scaleDelta) / newScale;
        this.#translate.y -= (distanceFromCenter.y * scaleDelta) / newScale;

        this.#scale = newScale;
        this.#requestUpdateTransform();

        if (this.#isZoomed()) {
          this.#startMonitoring();
        } else {
          this.#stopMonitoring();
        }
      }
    }
  };

  #handleClickZoom = (event) => {
    if (!this.#image) return;

    // Use the unified zoom system instead of direct CSS manipulation
    const zoomed = this.#scale > 1.1;

    if (!zoomed) {
      this.#scale = 2;
      this.#translate = { x: 0, y: 0 };
      this.#requestUpdateTransform();
      this.#startMonitoring();
    } else {
      this.#resetZoom();
    }
  };

  #resetZoom() {
    this.#scale = DEFAULT_ZOOM;
    this.#translate = { x: 0, y: 0 };
    this.#requestUpdateTransform();
    this.#stopMonitoring();
  }

  #findScrollParent(el) {
    let parent = el?.parentElement;
    while (parent && parent !== document.body && parent !== document.documentElement) {
      const style = getComputedStyle(parent);
      const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
      if (/(auto|scroll|overlay)/.test(overflow)) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return window;
  }

  #computeMaxTranslate(scale) {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions || !containerWidth || !containerHeight) {
      return { maxTranslateX: 0, maxTranslateY: 0 };
    }
    const { width: imageWidth, height: imageHeight } = imageDimensions;
    const scaledWidth = imageWidth * scale;
    const scaledHeight = imageHeight * scale;
    const excessWidth = Math.max(0, scaledWidth - containerWidth);
    const excessHeight = Math.max(0, scaledHeight - containerHeight);

    return {
      maxTranslateX: excessWidth / (2 * scale),
      maxTranslateY: excessHeight / (2 * scale),
    };
  }

  #stopMonitoring() {
    if (!this.#listenersAttached) {
      this.#stopPoll();
      return;
    }

    const scrollParent = this.#scrollParent;
    try {
      if (scrollParent && scrollParent !== window && scrollParent.removeEventListener) {
        scrollParent.removeEventListener('scroll', this.#onScrollParentScroll, { passive: true });
      } else {
        window.removeEventListener('scroll', this.#onScrollParentScroll, { passive: true });
      }
    } catch (e) {
      /* ignore */
    }

    try {
      document.removeEventListener('wheel', this.#onWheel, { capture: true });
      document.removeEventListener('touchmove', this.#onTouchMove, { capture: true });
    } catch (e) {
      /* ignore */
    }

    this.#listenersAttached = false;
    this.#scrollParent = null;
    this.#wheelAccum = 0;
    this.#lastScrollPos = 0;
    this.#lastBoundingTop = null;

    this.#stopPoll();
  }

  #onScrollParentScroll = (ev) => {
    if (!this.#isZoomed()) return;

    const pos =
      this.#scrollParent === window
        ? window.scrollY || document.scrollingElement?.scrollTop || 0
        : this.#scrollParent?.scrollTop || 0;

    const delta = Math.abs(pos - this.#lastScrollPos);
    if (delta > 0) this.#lastScrollPos = pos;

    if (delta > 250) {
      this.#resetZoom();
    }
  };

  #onWheel = (ev) => {
    if (!this.#isZoomed()) return;
    this.#wheelAccum += Math.abs(ev.deltaY || 0);

    if (this.#wheelAccum > 500) {
      this.#resetZoom();
    }
  };

  #onTouchMove = (ev) => {
    if (!this.#isZoomed()) return;
    if (this.#scrollParent && this.#scrollParent !== window) {
      const pos = this.#scrollParent.scrollTop || 0;
      const delta = Math.abs(pos - this.#lastScrollPos);
      if (delta > 120) {
        this.#resetZoom();
      }
    }
  };

  #startPoll() {
    if (this.#pollingActive) return;
    this.#pollingActive = true;
    const tick = () => {
      if (!this.#pollingActive) return;
      if (!this.#isZoomed()) {
        this.#stopPoll();
        return;
      }
      const rect = this.getBoundingClientRect();
      if (typeof this.#lastBoundingTop === 'number') {
        const delta = Math.abs(rect.top - this.#lastBoundingTop);
        if (delta > 20) {
          this.#resetZoom();
          return;
        }
      }
      this.#lastBoundingTop = rect.top;
      this.#pollRafId = requestAnimationFrame(tick);
    };
    this.#pollRafId = requestAnimationFrame(tick);
  }

  #stopPoll() {
    if (this.#pollRafId) {
      cancelAnimationFrame(this.#pollRafId);
      this.#pollRafId = null;
    }
    this.#pollingActive = false;
  }

  #isZoomed() {
    return this.#scale > 1.1;
  }

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      preventDefault(event);
      const [p1, p2] = Array.from(event.touches).map(touchToPoint);
      if (!p1 || !p2) {
        return;
      }
      this.#startZoomGesture(p1, p2);
    } else if (event.touches.length === 1) {
      const point = touchToPoint(event.touches[0]);
      if (!point) return;
      this.#startDragGesture(point);
    }
  };

  #startZoomGesture(point1, point2) {
    this.#initialDistance = getDistance(point1, point2);
    this.#startScale = this.#scale;
    this.#isPinching = true;
    this.#isDragging = false;
  }

  #startDragGesture(point) {
    if (this.#isPinching) return; // Don't start dragging while pinching
    this.#startPosition = { x: point.x, y: point.y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
  }

  #handleTouchMove = (event) => {
    const currentTouchCount = event.touches.length;

    // Handle transition from 1 touch to 2 touches (start pinch during drag)
    if (currentTouchCount === 2 && !this.#isPinching) {
      preventDefault(event);
      const [p1, p2] = Array.from(event.touches).map(touchToPoint);
      if (p1 && p2) {
        this.#startZoomGesture(p1, p2);
        this.#processZoomGesture(event);
      }
      return;
    }

    const isZooming = currentTouchCount === 2 && this.#isPinching;
    const isDragging = currentTouchCount === 1 && this.#isDragging && !this.#isPinching;

    if (isZooming) {
      preventDefault(event);
      this.#processZoomGesture(event);
      return;
    }

    if (!isDragging) return;

    const moved = this.#processDragGesture(event);

    if (moved) {
      preventDefault(event);
    } else {
      this.#isDragging = false;
    }
  };

  #processZoomGesture(event) {
    const [point1, point2] = Array.from(event.touches).map(touchToPoint);
    if (!point1 || !point2) return;

    const currentDistance = getDistance(point1, point2);
    const currentMidpoint = getMidpoint(point1, point2);

    // Calculate new scale
    const scaleRatio = currentDistance / this.#initialDistance;
    const newScale = this.#startScale * scaleRatio;
    const clampedScale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Get container center and touch midpoint relative to container
    const containerRect = this.getBoundingClientRect();
    const containerCenter = {
      x: containerRect.width / 2,
      y: containerRect.height / 2,
    };

    const relativeMidpoint = {
      x: currentMidpoint.x - containerRect.left,
      y: currentMidpoint.y - containerRect.top,
    };

    // Calculate zoom point offset from center
    const distanceFromCenter = {
      x: relativeMidpoint.x - containerCenter.x,
      y: relativeMidpoint.y - containerCenter.y,
    };

    // Adjust translation to zoom around the pinch center
    const scaleDelta = clampedScale / this.#scale - 1.0;
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / clampedScale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / clampedScale;

    this.#scale = clampedScale;
    this.#requestUpdateTransform();
  }

  #processDragGesture(event) {
    const point = touchToPoint(event.touches[0]);
    if (!point) return false;

    const proposed = {
      x: this.#startTranslate.x + (point.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (point.y - this.#startPosition.y) / this.#scale,
    };

    const { maxTranslateX, maxTranslateY } = this.#computeMaxTranslate(this.#scale);

    const clamped = {
      x: clamp(proposed.x, -maxTranslateX, maxTranslateX),
      y: clamp(proposed.y, -maxTranslateY, maxTranslateY),
    };

    const moved = clamped.x !== this.#translate.x || clamped.y !== this.#translate.y;
    if (moved) {
      this.#translate = clamped;
      this.#requestUpdateTransform();
    }

    return moved;
  }

  #handleTouchEnd = (event) => {
    const now = Date.now();

    // Reset pinch state when no touches remain
    if (event.touches.length === 0) {
      this.#isPinching = false;
      this.#isDragging = false;

      // Double-tap detection
      if (now - this.#lastTapTime < 300) {
        if (!this.#isZoomed()) {
          this.#scale = 2;
          this.#translate = { x: 0, y: 0 };
          this.#requestUpdateTransform();
          this.#startMonitoring();
        } else {
          this.#resetZoom();
        }
        this.#lastTapTime = 0;
        return; // Exit early to prevent setting lastTapTime again
      } else {
        this.#lastTapTime = now;
      }

      this.#requestUpdateTransform();

      // Start monitoring if zoomed
      if (this.#isZoomed()) {
        this.#startMonitoring();
      }
    }
    // Handle transition from pinch to drag
    else if (event.touches.length === 1 && this.#isPinching) {
      this.#isPinching = false;
      const point = touchToPoint(event.touches[0]);
      if (point) {
        this.#startDragGesture(point);
      }
    }
  };

  #startMonitoring() {
    if (this.#listenersAttached) return;

    const scrollParent = this.#findScrollParent(this) || window;
    this.#scrollParent = scrollParent;
    this.#lastScrollPos =
      scrollParent === window
        ? window.scrollY || document.scrollingElement?.scrollTop || 0
        : scrollParent.scrollTop || 0;

    if (scrollParent && scrollParent !== window && scrollParent.addEventListener) {
      scrollParent.addEventListener('scroll', this.#onScrollParentScroll, { passive: true });
    } else {
      window.addEventListener('scroll', this.#onScrollParentScroll, { passive: true });
    }

    document.addEventListener('wheel', this.#onWheel, { passive: true, capture: true });

    this.#listenersAttached = true;
  }

  #getMinZoom = () => {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions || !containerWidth || !containerHeight) return MIN_ZOOM;
    const { width: imageWidth, height: imageHeight } = imageDimensions;

    // Only enforce minimum zoom if image is significantly larger than container
    // This prevents tiny images from being forced to scale up
    const scaleToFitWidth = containerWidth / imageWidth;
    const scaleToFitHeight = containerHeight / imageHeight;
    const scaleToFit = Math.min(scaleToFitWidth, scaleToFitHeight);

    // Only use a minZoom greater than 1 if the image would be too small to see
    const minZoom = scaleToFit < 0.5 ? scaleToFit + 0.001 : MIN_ZOOM;
    return minZoom;
  };

  #getImageDimensions = () => {
    const containerRect = this.getBoundingClientRect();
    if (!this.#image) return null;
    const { naturalWidth, naturalHeight } = this.#image;
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) return null;

    const containerAspect = containerRect.width / containerRect.height;
    const naturalAspect = naturalWidth / naturalHeight;

    let imageWidth, imageHeight;
    if (naturalAspect > containerAspect) {
      imageWidth = containerRect.width;
      imageHeight = imageWidth / naturalAspect;
    } else {
      imageHeight = containerRect.height;
      imageWidth = imageHeight * naturalAspect;
    }

    if (imageWidth === 0 || imageHeight === 0) return null;

    return { width: imageWidth, height: imageHeight };
  };

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight) return;

    const minZoom = this.#getMinZoom();

    if (minZoom) {
      this.#scale = clamp(this.#scale, minZoom, MAX_ZOOM);
    }

    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions) return;

    const { width: imageWidth, height: imageHeight } = imageDimensions;

    const scaledWidth = imageWidth * this.#scale;
    const scaledHeight = imageHeight * this.#scale;

    const excessWidth = Math.max(0, scaledWidth - containerWidth);
    const excessHeight = Math.max(0, scaledHeight - containerHeight);

    const maxTranslateX = excessWidth / (2 * this.#scale);
    const maxTranslateY = excessHeight / (2 * this.#scale);

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

function touchToPoint(touch) {
  if (!touch) return undefined;
  return { x: touch.clientX, y: touch.clientY };
}

function getDistance(point1, point2) {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function getMidpoint(point1, point2) {
  return {
    x: (point1.x + point2.x) / 2,
    y: (point1.y + point2.y) / 2,
  };
}